import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { API_URL } from "../../config";
import { showToast } from "../../components/toast";
import Swal from "sweetalert2";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faChevronLeft,
  faChevronRight,
  faComment,
  faEye,
  faDownload,
  faSort,
  faSortUp,
  faSortDown,
  faPaperPlane,
} from "@fortawesome/free-solid-svg-icons";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import LoadingSpinner from "../../components/LoadingSpinner";
import './Portfolio.css';
import { PermissionWrapper, hasPermission } from "../../helpers/permission";
import { convertToEnc } from "../../utils/cryptoEnc";

const Portfolio = () => {
  const [portfolios, setPortfolios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [selectedPortfolioId, setSelectedPortfolioId] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [bulkExporting, setBulkExporting] = useState(false);
  const [encryptedBulkExporting, setEncryptedBulkExporting] = useState(false);
  const [sendingCareparrvaId, setSendingCareparrvaId] = useState(null);
  const [commentForm, setCommentForm] = useState({
    heading: "",
    message: "",
    files: null,
  });

  // 🔎 Search Filters
  const [typeFilter, setTypeFilter] = useState("");
  const [nameFilter, setNameFilter] = useState("");
  const [philosophyNameFilter, setPhilosophyNameFilter] = useState("");
  const [horizonFilter, setHorizonFilter] = useState("");
  const [minInvestmentFilter, setMinInvestmentFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [lastRebalanceFilter, setLastRebalanceFilter] = useState("");
  const [nextRebalanceFilter, setNextRebalanceFilter] = useState("");
  const [cagrFilter, setCagrFilter] = useState("");
  const [cagr1wFilter, setCagr1wFilter] = useState("");
  const [cagr1Filter, setCagr1Filter] = useState("");
  const [cagr3Filter, setCagr3Filter] = useState("");
  const [cagr6Filter, setCagr6Filter] = useState("");
  const [cagrTillDateFilter, setCagrTillDateFilter] = useState("");
  const [uniqueIdFilter, setUniqueIdFilter] = useState("");
  const tableContainerRef = useRef(null);
  const topScrollbarRef = useRef(null);

  // 📑 Pagination
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 100;

  const [sortField, setSortField] = useState("");
  const [sortOrder, setSortOrder] = useState("");

  const sortBox = (field) => {
    if (sortField === field) {
      const newOrder = sortOrder === "asc" ? "desc" : "asc";
      setSortOrder(newOrder);
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const getSortIcon = (field) => {
    if (sortField === field) {
      return sortOrder === "asc" ? faSortUp : faSortDown;
    }
    return faSort;
  };

  // Fetch portfolios
  useEffect(() => {
    const fetchPortfolios = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("authToken");
        const response = await axios.get(`${API_URL}portfolio/serviceList`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.data && response.data.success) {
          setPortfolios(response.data.data || []);
        } else {
          setPortfolios([]);
        }
      } catch (err) {
        console.error("Error fetching portfolios:", err);
        showToast.error("Failed to load portfolios");
      } finally {
        setLoading(false);
      }
    };

    fetchPortfolios();
  }, []);


  useEffect(() => {
    const handleClearFilters = () => {
      setTypeFilter("");
      setNameFilter("");
      setPhilosophyNameFilter("");
      setHorizonFilter("");
      setMinInvestmentFilter("");
      setStatusFilter("");
      setLastRebalanceFilter("");
      setNextRebalanceFilter("");
      setCagrFilter("");
      setCagr1wFilter("");
      setCagr1Filter("");
      setCagr3Filter("");
      setCagr6Filter("");
      setCagrTillDateFilter("");
    };

    window.addEventListener('clearPortfolioFiltersEvent', handleClearFilters);

    return () => {
      window.removeEventListener('clearPortfolioFiltersEvent', handleClearFilters);
    };
  }, []);

  const isSyncingScrollRef = useRef(false);
  const lastTableScrollLeftRef = useRef(0);

  const syncScroll = () => {
    if (tableContainerRef.current && topScrollbarRef.current) {
      const currentLeft = tableContainerRef.current.scrollLeft;
      if (currentLeft === lastTableScrollLeftRef.current) return;
      lastTableScrollLeftRef.current = currentLeft;
      if (isSyncingScrollRef.current) return;
      isSyncingScrollRef.current = true;
      topScrollbarRef.current.scrollLeft = currentLeft;
      requestAnimationFrame(() => {
        isSyncingScrollRef.current = false;
      });
    }
  };

  const onTopScroll = () => {
    if (tableContainerRef.current && topScrollbarRef.current) {
      const currentLeft = topScrollbarRef.current.scrollLeft;
      if (isSyncingScrollRef.current) return;
      isSyncingScrollRef.current = true;
      tableContainerRef.current.scrollLeft = currentLeft;
      lastTableScrollLeftRef.current = currentLeft;
      requestAnimationFrame(() => {
        isSyncingScrollRef.current = false;
      });
    }
  };


  // 🔎 Apply filters
  const filteredPortfolios = useMemo(() => {
    let result = portfolios.filter((p) => {
      if (
        typeFilter &&
        !(p.segment_name?.toLowerCase() || "").includes(typeFilter.toLowerCase())
      )
        return false;

      if (
        nameFilter &&
        !(p.service_name?.toLowerCase() || "").includes(nameFilter.toLowerCase())
      )
        return false;

      if (
        philosophyNameFilter &&
        !(p.philosophy_name?.toLowerCase() || "").includes(philosophyNameFilter.toLowerCase())
      )
        return false;

      if (
        horizonFilter &&
        !(p.investment_period?.toLowerCase() || "").includes(horizonFilter.toLowerCase())
      )
        return false;

      if (
        minInvestmentFilter &&
        !(p.min_investment?.toString() || "").includes(minInvestmentFilter)
      )
        return false;

      if (statusFilter && !(p.activate === Number(statusFilter))) return false;

      // ✅ Fix for Last Rebalance Date Filter
      if (lastRebalanceFilter && p.last_rebalance_date) {
        const lastDate = new Date(p.last_rebalance_date);
        const formattedLast = lastDate.toLocaleDateString("en-CA"); // yyyy-mm-dd
        if (formattedLast !== lastRebalanceFilter) return false;
      }

      // ✅ Fix for Next Rebalance Date Filter
      if (nextRebalanceFilter && p.next_rebalance_date) {
        const nextDate = new Date(p.next_rebalance_date);
        const formattedNext = nextDate.toLocaleDateString("en-CA"); // yyyy-mm-dd
        if (formattedNext !== nextRebalanceFilter) return false;
      }

      if (
        cagrFilter &&
        !(p.cagr?.toString() || "").includes(cagrFilter)
      )
        return false;

      if (cagr1wFilter && !(p.cagr_1w?.toString() || "").includes(cagr1wFilter))
        return false;

      if (cagr1Filter && !(p.cagr_1?.toString() || "").includes(cagr1Filter))
        return false;

      if (cagr3Filter && !(p.cagr_3?.toString() || "").includes(cagr3Filter))
        return false;

      if (cagr6Filter && !(p.cagr_6?.toString() || "").includes(cagr6Filter))
        return false;

      if (
        cagrTillDateFilter &&
        !(p.cagr_till_date?.toString() || "").includes(cagrTillDateFilter)
      )
        return false;

      if (
        uniqueIdFilter &&
        !(p.unique_id?.toLowerCase() || "").includes(uniqueIdFilter.toLowerCase())
      )
        return false;

      return true;
    });

    if (sortField) {
      result = [...result].sort((a, b) => {
        let valA = a[sortField];
        let valB = b[sortField];

        if (valA == null) valA = "";
        if (valB == null) valB = "";

        if (valA === valB) return 0;
        
        // Handle numbers
        if (typeof valA === 'number' && typeof valB === 'number') {
            return sortOrder === "asc" ? valA - valB : valB - valA;
        }

        // Handle strings that could be numbers
        const numA = Number(valA);
        const numB = Number(valB);
        if (!isNaN(numA) && !isNaN(numB) && valA !== "" && valB !== "") {
            return sortOrder === "asc" ? numA - numB : numB - numA;
        }

        // String comparison
        const strA = String(valA).toLowerCase();
        const strB = String(valB).toLowerCase();
        
        if (strA < strB) return sortOrder === "asc" ? -1 : 1;
        if (strA > strB) return sortOrder === "asc" ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [
    portfolios,
    typeFilter,
    nameFilter,
    philosophyNameFilter,
    horizonFilter,
    minInvestmentFilter,
    statusFilter,
    lastRebalanceFilter,
    nextRebalanceFilter,
    cagrFilter,
    cagr1wFilter,
    cagr1Filter,
    cagr3Filter,
    cagr6Filter,
    cagrTillDateFilter,
    uniqueIdFilter,
    sortField,
    sortOrder
  ]);


  // 📑 Update pagination
  useEffect(() => {
    setTotal(filteredPortfolios.length);
    setPageCount(Math.ceil(filteredPortfolios.length / limit));
    setPage(1);
  }, [filteredPortfolios, limit]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pageCount) {
      setPage(newPage);
    }
  };

  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);
  const paginatedPortfolios = filteredPortfolios.slice(start - 1, end);

  useEffect(() => {
    const tableWidth = tableContainerRef.current?.scrollWidth || 0;
    const containerWidth = tableContainerRef.current?.clientWidth || 0;
    if (topScrollbarRef.current) {
      topScrollbarRef.current.style.width = containerWidth + "px";
      topScrollbarRef.current.firstChild.style.width = tableWidth + "px";
    }
  }, [paginatedPortfolios]);


  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("authToken");
      const formDataToSend = new FormData();
      formDataToSend.append("service_id", selectedPortfolioId);
      formDataToSend.append("heading", commentForm.heading);
      formDataToSend.append("message", commentForm.message);
      if (commentForm.files) {
        formDataToSend.append("files", commentForm.files);
      }

      await axios.post(`${API_URL}portfolio/message/save`, formDataToSend, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      showToast.success("Message added successfully!");
      setShowCommentModal(false);
      setCommentForm({
        heading: "",
        message: "",
        file: null,
      });
    } catch (err) {
      console.error("Error adding Message:", err);
      showToast.error(err.response?.data?.message || "Failed to add Message");
    }
  };

  const handleCommentInputChange = (e) => {
    const { name, value, files } = e.target;
    setCommentForm({
      ...commentForm,
      [name]: files ? files[0] : value,
    });
  };

  const openCommentModal = (id) => {
    setSelectedPortfolioId(id);
    setShowCommentModal(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };


  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const handleExportPortfolios = async () => {
    try {
      setExporting(true);
      const token = localStorage.getItem("authToken");
      const response = await axios.get(`${API_URL}portfolio/portfolioExport`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });

      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      
      // Extract filename from Content-Disposition header or use default
      const contentDisposition = response.headers["content-disposition"];
      let filename = "portfolios.xlsx";
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="?([^";\n]+)"?/);
        if (fileNameMatch) {
          filename = fileNameMatch[1];
        }
      }
      
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      showToast.success("Portfolios exported successfully!");
    } catch (err) {
      console.error("Error exporting portfolios:", err);
      showToast.error(err.response?.data?.message || "Failed to export portfolios");
    } finally {
      setExporting(false);
    }
  };

  const handleBulkExport = async () => {
    try {
      setBulkExporting(true);
      const token = localStorage.getItem("authToken");
      const response = await axios.get(`${API_URL}portfolio/bulkExport`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });

      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;

      const contentDisposition = response.headers["content-disposition"];
      let filename = "Portfolio_Bulk_Export.xlsx";
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="?([^";\n]+)"?/);
        if (fileNameMatch) {
          filename = fileNameMatch[1];
        }
      }

      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showToast.success("Portfolio bulk data exported successfully!");
    } catch (err) {
      console.error("Error bulk exporting portfolio data:", err);
      showToast.error(err.response?.data?.message || "Failed to bulk export portfolio data");
    } finally {
      setBulkExporting(false);
    }
  };

  const handleEncryptedBulkExport = async () => {
    try {
      setEncryptedBulkExporting(true);
      const token = localStorage.getItem("authToken");
      const response = await axios.get(`${API_URL}portfolio/bulkExport?divideWeightage=true`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });

      let filename = "Portfolio_Bulk_Export.xlsx";
      const contentDisposition = response.headers["content-disposition"];
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="?([^";\n]+)"?/);
        if (fileNameMatch) {
          filename = fileNameMatch[1];
        }
      }

      const excelFile = new File([response.data], filename, {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      // Convert to .enc using CarePARVA encryption
      const encFile = await convertToEnc(excelFile);

      // Create blob link to download .enc file
      const url = window.URL.createObjectURL(encFile);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", encFile.name || "Portfolio_Bulk_Export.enc");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showToast.success("Encrypted portfolio bulk data exported successfully!");
    } catch (err) {
      console.error("Error exporting encrypted portfolio bulk data:", err);
      showToast.error(err.response?.data?.message || err.message || "Failed to export encrypted portfolio bulk data");
    } finally {
      setEncryptedBulkExporting(false);
    }
  };

  const handleDownloadFactsheet = async (portfolioId, existingFactsheetUrl) => {
    try {
      let factsheetUrl = existingFactsheetUrl;
      if (!factsheetUrl) {
        const token = localStorage.getItem("authToken");
        const response = await axios.get(
          `${API_URL}portfolio/details/${portfolioId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: "*/*",
            },
          }
        );
        factsheetUrl = response.data?.service?.factsheet_url;
      }

      if (!factsheetUrl) {
        showToast.error("Factsheet not available for this portfolio.");
        return;
      }

      window.open(factsheetUrl, "_blank", "noopener,noreferrer");
    } catch (error) {
      console.error("Error downloading factsheet:", error);
      showToast.error("Failed to download factsheet");
    }
  };

  const handleSendCareparrva = async (id, serviceName) => {
    const confirmResult = await Swal.fire({
      title: "Send to Careparrva?",
      text: `Are you sure you want to send "${serviceName}" portfolio advice to Careparrva PDC?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#6f42c1",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, Send!",
    });

    if (!confirmResult.isConfirmed) return;

    try {
      setSendingCareparrvaId(id);
      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        `${API_URL}portfolio/sendCareparrvaPortfolio/${id}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data && response.data.success) {
        Swal.fire("Success!", response.data.message || "Portfolio advice sent to Careparrva successfully", "success");
        showToast.success("Portfolio advice sent to Careparrva successfully");
      } else {
        Swal.fire("Failed", response.data?.message || response.data?.error || "Failed to send portfolio to Careparrva", "error");
        showToast.error(response.data?.message || "Failed to send portfolio to Careparrva");
      }
    } catch (err) {
      console.error("Error sending portfolio to Careparrva:", err);
      const errorMsg = err.response?.data?.message || err.message || "Failed to send portfolio to Careparrva";
      Swal.fire("Error", typeof errorMsg === "object" ? JSON.stringify(errorMsg) : errorMsg, "error");
      showToast.error(typeof errorMsg === "object" ? JSON.stringify(errorMsg) : errorMsg);
    } finally {
      setSendingCareparrvaId(null);
    }
  };

  return (
    <div id="page-container" className="page-header-fixed page-sidebar-fixed fade in">
      <div id="content" className="">
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 pb-2">
          <div>
            <ul className="breadcrumb mb-0">
              <li className="breadcrumb-item">
                <a href="/Dashboard">
                  <i className="ti-home"></i> Home
                </a>
              </li>
              <li className="breadcrumb-item active">Manage Portfolio</li>
            </ul>
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <a
              href="https://sgapp-data.s3.ap-south-1.amazonaws.com/media/certificate/Certificate_Verific_Past%20PerformRA_Jun2026%20-f.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-default btn-sm text-nowrap"
              title="Download CA Certificate"
            >
              <FontAwesomeIcon icon={faDownload} /> Download CA Certificate
            </a>
            <PermissionWrapper module="portfolio" action="add">
              <Link
                to={"/research/Services/portfolio/add"}
                className="btn btn-default btn-sm text-nowrap"
              >
                <FontAwesomeIcon icon={faPlus} /> Add Portfolio Based Service
              </Link>
            </PermissionWrapper>
            <PermissionWrapper module="portfolio" action="export">
              <button
                onClick={handleExportPortfolios}
                disabled={exporting}
                className="btn btn-default btn-sm text-nowrap"
                title="Export Portfolios"
              >
                <FontAwesomeIcon icon={faDownload} /> {exporting ? "Exporting..." : "Export Portfolios"}
              </button>
            </PermissionWrapper>
            {(hasPermission("portfolio_bulk_export", "view") || hasPermission("portfolio", "export")) && (
              <button
                onClick={handleBulkExport}
                disabled={bulkExporting}
                className="btn btn-default btn-sm text-nowrap"
                title="Bulk Export"
              >
                <FontAwesomeIcon icon={faDownload} /> {bulkExporting ? "Exporting..." : "Bulk Export"}
              </button>
            )}
            {(hasPermission("portfolio_encrypted_bulk_export", "view") || hasPermission("portfolio", "export")) && (
              <button
                onClick={handleEncryptedBulkExport}
                disabled={encryptedBulkExporting}
                className="btn btn-default btn-sm text-nowrap"
                title="Encrypted Bulk Export"
              >
                <FontAwesomeIcon icon={faDownload} /> {encryptedBulkExporting ? "Exporting Encrypted..." : "Encrypted Bulk Export"}
              </button>
            )}
          </div>
        </div>

        <div className="panel panel-default with-rounded-corner with-shadow p-10">
          {/* 📑 Pagination Controls */}
          {loading || portfolios.length > 0 && (
            <div className="d-flex align-items-center justify-content-between p-3 border-bottom">
              <h4 className="m-0">Portfolio Services</h4>
              <div>
                <span style={{ fontSize: "0.8rem" }} className="me-2">
                  {start} to {end} of {total}
                </span>
                <button
                  className="btn btn-default btn-sm"
                  title="Previous"
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page === 1}
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </button>
                &nbsp;
                <button
                  className="btn btn-default btn-sm"
                  title="Next"
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page === pageCount}
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </button>
              </div>
            </div>
          )}

          {loading || portfolios.length > 0 ? (
            <>
              <div
                ref={topScrollbarRef}
                onScroll={onTopScroll}
                style={{
                  overflowX: "auto",
                  overflowY: "hidden",
                  height: "16px",
                  marginBottom: "4px",
                }}
              >
                {/* Child div to set scrollbar width */}
                <div style={{ height: "1px" }}></div>
              </div>
              <div className="table-responsive portfolio-table-container" ref={tableContainerRef} onScroll={syncScroll} style={{ maxHeight: "calc(100vh - 200px)", overflow: "auto" }}>
                <table className="table table-condensed table-bordered bg-white m-0 portfolio-table">
                  <thead>
                    <tr>
                      <th style={{ textAlign: "center" }}>#</th>
                      <th>Image</th>
                      <th style={{ cursor: "pointer", whiteSpace: "nowrap" }} onClick={() => sortBox("segment_name")}>Portfolio Type <FontAwesomeIcon icon={getSortIcon("segment_name")} /></th>
                      <th style={{ cursor: "pointer", whiteSpace: "nowrap" }} onClick={() => sortBox("service_name")}>Portfolio Name <FontAwesomeIcon icon={getSortIcon("service_name")} /></th>
                      <th style={{ cursor: "pointer", whiteSpace: "nowrap" }} onClick={() => sortBox("unique_id")}>Unique ID <FontAwesomeIcon icon={getSortIcon("unique_id")} /></th>
                      <th style={{ cursor: "pointer", whiteSpace: "nowrap" }} onClick={() => sortBox("philosophy_name")}>Philosophy Name <FontAwesomeIcon icon={getSortIcon("philosophy_name")} /></th>
                      <th style={{ cursor: "pointer", whiteSpace: "nowrap" }} onClick={() => sortBox("investment_period")}>Investment Horizon <FontAwesomeIcon icon={getSortIcon("investment_period")} /></th>
                      <th style={{ cursor: "pointer", whiteSpace: "nowrap" }} onClick={() => sortBox("last_rebalance_date")}>Last Rebalance <FontAwesomeIcon icon={getSortIcon("last_rebalance_date")} /></th>
                      <th style={{ cursor: "pointer", whiteSpace: "nowrap" }} onClick={() => sortBox("next_rebalance_date")}>Next Rebalance<FontAwesomeIcon icon={getSortIcon("next_rebalance_date")} /></th>
                      <th style={{ cursor: "pointer", whiteSpace: "nowrap" }} onClick={() => sortBox("min_investment")}>Min Investment <FontAwesomeIcon icon={getSortIcon("min_investment")} /></th>
                      <th style={{ cursor: "pointer", whiteSpace: "nowrap" }} onClick={() => sortBox("cagr")}>CAGR <FontAwesomeIcon icon={getSortIcon("cagr")} /></th>
                      {[
                        ["cagr_1w", "1W"],
                        ["cagr_1", "1M"],
                        ["cagr_3", "3M"],
                        ["cagr_6", "6M"],
                      ].map(([field, label]) => (
                        <th key={field} style={{ cursor: "pointer", whiteSpace: "nowrap" }} onClick={() => sortBox(field)}>
                          {label} <FontAwesomeIcon icon={getSortIcon(field)} />
                        </th>
                      ))}
                      <th style={{ cursor: "pointer", whiteSpace: "nowrap" }} onClick={() => sortBox("cagr_till_date")}>CAGR Till Date <FontAwesomeIcon icon={getSortIcon("cagr_till_date")} /></th>
                      <th style={{ cursor: "pointer", whiteSpace: "nowrap" }} onClick={() => sortBox("activate")}>Status <FontAwesomeIcon icon={getSortIcon("activate")} /></th>
                      <th style={{ cursor: "pointer", whiteSpace: "nowrap" }} onClick={() => sortBox("access_price_daily")}>Per Day <FontAwesomeIcon icon={getSortIcon("access_price_daily")} /></th>
                      <th style={{ cursor: "pointer", whiteSpace: "nowrap" }} onClick={() => sortBox("access_price_monthly")}>Per Month <FontAwesomeIcon icon={getSortIcon("access_price_monthly")} /></th>
                      <th style={{ cursor: "pointer", whiteSpace: "nowrap" }} onClick={() => sortBox("discount_price_monthly")}>Disc Per Month <FontAwesomeIcon icon={getSortIcon("discount_price_monthly")} /></th>
                      <th>Action</th>
                    </tr>
                    {/* 🔎 Filters */}
                    <tr>
                      <th></th>
                      <th></th>
                      <th>
                        <input
                          type="text"
                          className="form-control input-sm"
                          placeholder="Search type..."
                          value={typeFilter}
                          onChange={(e) => setTypeFilter(e.target.value)}
                          style={{ width: "100%", fontSize: "12px" }}
                        />
                      </th>
                      <th>
                        <input
                          type="text"
                          className="form-control input-sm"
                          placeholder="Search name..."
                          value={nameFilter}
                          onChange={(e) => setNameFilter(e.target.value)}
                          style={{ width: "100%", fontSize: "12px" }}
                        />
                      </th>
                      <th>
                        <input
                          type="text"
                          className="form-control input-sm"
                          placeholder="Search Unique ID..."
                          value={uniqueIdFilter}
                          onChange={(e) => setUniqueIdFilter(e.target.value)}
                          style={{ width: "100%", fontSize: "12px" }}
                        />
                      </th>
                      <th>
                        <input
                          type="text"
                          className="form-control input-sm"
                          placeholder="Search philosophy..."
                          value={philosophyNameFilter}
                          onChange={(e) => setPhilosophyNameFilter(e.target.value)}
                          style={{ width: "100%", fontSize: "12px" }}
                        />
                      </th>
                      <th>
                        <input
                          type="text"
                          className="form-control input-sm"
                          placeholder="Search horizon..."
                          value={horizonFilter}
                          onChange={(e) => setHorizonFilter(e.target.value)}
                          style={{ width: "100%", fontSize: "12px" }}
                        />
                      </th>
                      <th>
                        <input
                          type="date"
                          className="form-control input-sm"
                          value={lastRebalanceFilter}
                          onChange={(e) => setLastRebalanceFilter(e.target.value)}
                          style={{ width: "100%", fontSize: "12px" }}
                        />
                      </th>
                      <th>
                        <input
                          type="date"
                          className="form-control input-sm"
                          value={nextRebalanceFilter}
                          onChange={(e) => setNextRebalanceFilter(e.target.value)}
                          style={{ width: "100%", fontSize: "12px" }}
                        />
                      </th>
                      <th>
                        <input
                          type="text"
                          className="form-control input-sm"
                          placeholder="Search investment..."
                          value={minInvestmentFilter}
                          onChange={(e) => setMinInvestmentFilter(e.target.value)}
                          style={{ width: "100%", fontSize: "12px" }}
                        />
                      </th>
                      <th>
                        <input
                          type="text"
                          className="form-control input-sm"
                          placeholder="Search CAGR..."
                          value={cagrFilter}
                          onChange={(e) => setCagrFilter(e.target.value)}
                          style={{ width: "100%", fontSize: "12px" }}
                        />
                      </th>
                      {[
                        ["1W", cagr1wFilter, setCagr1wFilter],
                        ["1M", cagr1Filter, setCagr1Filter],
                        ["3M", cagr3Filter, setCagr3Filter],
                        ["6M", cagr6Filter, setCagr6Filter],
                      ].map(([label, value, setValue]) => (
                        <th key={label}>
                          <input
                            type="text"
                            className="form-control input-sm"
                            placeholder="Search"
                            value={value}
                            onChange={(e) => setValue(e.target.value)}
                            style={{ width: "100%", fontSize: "12px" }}
                          />
                        </th>
                      ))}
                      <th>
                        <input
                          type="text"
                          className="form-control input-sm"
                          placeholder="Search CAGR TD..."
                          value={cagrTillDateFilter}
                          onChange={(e) => setCagrTillDateFilter(e.target.value)}
                          style={{ width: "100%", fontSize: "12px" }}
                        />
                      </th>
                      {/* <th></th> */}
                      <th>
                        <select
                          className="input-sm"
                          value={statusFilter}
                          onChange={(e) => setStatusFilter(e.target.value)}
                          style={{ width: "100%", fontSize: "12px" }}
                        >
                          <option value="">All</option>
                          <option value="1">Active</option>
                          <option value="0">Inactive</option>
                        </select>
                      </th>
                      <th></th>
                      <th></th>
                      <th></th>
                      <th>
                        <button
                          className="btn btn-default btn-sm"
                          onClick={() => {
                            setTypeFilter("");
                            setNameFilter("");
                            setPhilosophyNameFilter("");
                            setHorizonFilter("");
                            setMinInvestmentFilter("");
                            setStatusFilter("");
                            setLastRebalanceFilter("");
                            setNextRebalanceFilter("");
                            setCagrFilter("");
                            setCagr1wFilter("");
                            setCagr1Filter("");
                            setCagr3Filter("");
                            setCagr6Filter("");
                            setCagrTillDateFilter("");
                            setUniqueIdFilter("");
                          }}
                          style={{ fontSize: "12px", padding: "2px 6px" }}
                        >
                          Clear
                        </button>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      Array.from({ length: 10 }).map((_, rowIndex) => (
                        <tr key={`skeleton-${rowIndex}`} className="skeleton-row">
                          {Array.from({ length: 21 }).map((_, colIndex) => (
                            <td key={colIndex}>
                              <div className="skeleton-box"></div>
                            </td>
                          ))}
                        </tr>
                      ))
                    ) : paginatedPortfolios.length > 0 ? (
                      paginatedPortfolios.map((portfolio, index) => (
                        <tr key={portfolio.id}>
                          <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                            {(page - 1) * limit + index + 1}
                          </td>
                          <td style={{ textAlign: "center" }}>
                            {portfolio.service_image ? (
                              <img
                                src={portfolio.service_image}
                                alt="service img"
                                style={{ width: 24, height: 24, objectFit: "contain" }}
                              />
                            ) : (
                              <span></span>
                            )}
                          </td>
                          <td title={portfolio.segment_name}>{portfolio.segment_name}</td>
                          <td title={portfolio.service_name}>{portfolio.service_name}</td>
                          <td title={portfolio.unique_id}>{portfolio.unique_id || "-"}</td>
                          <td title={portfolio.philosophy_name}>{portfolio.philosophy_name}</td>
                          <td title={portfolio.investment_period}>{portfolio.investment_period}</td>
                          <td>{formatDate(portfolio.last_rebalance_date)}</td>
                          <td>{formatDate(portfolio.next_rebalance_date)}</td>
                          <td style={{ textAlign: "right" }}>{formatCurrency(portfolio.min_investment)}</td>
                          <td style={{ textAlign: "right" }}>{portfolio.cagr}%</td>
                          {[
                            "cagr_1w",
                            "cagr_1",
                            "cagr_3",
                            "cagr_6",
                          ].map((field) => (
                            <td
                              key={field}
                              style={{
                                textAlign: "right",
                                color: Number(portfolio[field]) >= 0 ? "green" : "red",
                                fontWeight: "bold",
                              }}
                            >
                              {portfolio[field] !== null && portfolio[field] !== undefined
                                ? `${portfolio[field]}%`
                                : "-"}
                            </td>
                          ))}
                          <td style={{ textAlign: "right" }}>{portfolio.cagr_till_date}</td>
                          <td style={{ textAlign: "center" }}>
                            {portfolio.activate === 1 ? (
                              <span className="badge bg-success">Active</span>
                            ) : (
                              <span className="badge bg-danger">Inactive</span>
                            )}
                          </td>
                          <td style={{ textAlign: "right" }}>{formatCurrency(portfolio.access_price_daily || 0)}</td>
                          <td style={{ textAlign: "right" }}>{formatCurrency(portfolio.access_price_monthly)}</td>
                          <td style={{ textAlign: "right" }}>{formatCurrency(portfolio.discount_price_monthly)}</td>
                          <td>
                            <div className="action-buttons">
                              <PermissionWrapper module="portfolio" action="view">
                                <button
                                  onClick={() => handleDownloadFactsheet(portfolio.id, portfolio.factsheet_url)}
                                  className="btn btn-success btn-xs"
                                  title="Download Factsheet"
                                >
                                  <FontAwesomeIcon icon={faDownload} />
                                </button>
                              </PermissionWrapper>
                              <PermissionWrapper module="portfoliomessage" action="add">
                                <button
                                  onClick={() => openCommentModal(portfolio.id)}
                                  className="btn btn-warning btn-xs"
                                  title="Comment"
                                >
                                  <FontAwesomeIcon icon={faComment} />
                                </button>
                              </PermissionWrapper>
                              <PermissionWrapper module="portfoliomessage" action="view">
                                <Link
                                  to={`/research/Portfolio/message/${portfolio.id}`}
                                  className="btn btn-info btn-xs"
                                  title="View Messages"
                                >
                                  <FontAwesomeIcon icon={faEye} />
                                </Link>
                              </PermissionWrapper>
                              <PermissionWrapper module="portfolioinfo" action="view">
                                <Link
                                  to={`/research/Portfolio/details/${portfolio.id}`}
                                  className="btn btn-primary btn-xs"
                                  title="View"
                                >
                                  <FontAwesomeIcon icon={faEye} />
                                </Link>
                              </PermissionWrapper>
                              <PermissionWrapper module="portfolio" action="edit">
                                <Link
                                  to={`/research/Services/portfolio/edit/${portfolio.id}`}
                                  className="btn btn-warning btn-xs"
                                  title="Edit Portfolio Service"
                                >
                                  <i className="ti ti-pencil"></i>
                                </Link>
                              </PermissionWrapper>
                              <PermissionWrapper module="portfolioinfo" action="view">
                                <Link
                                  to={`/research/PortfolioaddPage/${portfolio.id}`}
                                  className="btn btn-info btn-xs"
                                  title="View Report"
                                >
                                  <i className="ti ti-book"></i>
                                </Link>
                              </PermissionWrapper>                            
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="17" className="text-center">
                          No Records Found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="text-center p-4">
              <p>No portfolios found</p>
            </div>
          )}
        </div>
      </div>

      {/* Comment Modal */}
      {showCommentModal && (
        <div
          className="modal fade show"
          style={{ display: "block" }}
          id="commentModal"
          tabIndex="-1"
          role="dialog"
        >
          <div className="modal-dialog modal-xl" role="document">
            <div className="modal-content">
              <div className="modal-header d-flex align-items-center">
                <h5 className="modal-title">Add Message</h5>
                <button
                  type="button"
                  className="btn-close ms-auto"
                  aria-label="Close"
                  onClick={() => setShowCommentModal(false)}
                ></button>
              </div>

              <form onSubmit={handleCommentSubmit} encType="multipart/form-data">
                <div className="modal-body">
                  <input
                    type="hidden"
                    name="service_id"
                    value={selectedPortfolioId}
                  />
                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group">
                        <label className="control-label">Heading</label>
                        <input
                          type="text"
                          name="heading"
                          className="form-control"
                          placeholder="Enter Heading"
                          value={commentForm.heading}
                          onChange={handleCommentInputChange}
                          required
                        />
                      </div>
                    </div>

                    <div className="col-md-12">
                      <div className="form-group">
                        <label className="control-label">Message</label>
                        <ReactQuill
                          theme="snow"
                          value={commentForm.message}
                          onChange={(content) =>
                            setCommentForm({ ...commentForm, message: content })
                          }
                          modules={{
                            toolbar: [
                              ["bold", "italic", "underline"],
                              [{ list: "ordered" }, { list: "bullet" }],
                              ["link", "image"],
                              ["clean"],
                            ],
                          }}
                        />
                      </div>
                    </div>

                    <div className="col-md-12">
                      <div className="form-group">
                        <label className="control-label">Upload Image</label>
                        <input
                          type="file"
                          name="files"
                          className="form-control"
                          onChange={handleCommentInputChange}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowCommentModal(false)}
                  >
                    Close
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Save Message
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {showCommentModal && <div className="modal-backdrop fade show"></div>}

      <a href="#" data-click="scroll-top" className="btn-scroll-top fade">
        <i className="ti-arrow-up"></i>
      </a>
    </div>
  );
};

export default Portfolio;
